import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import {
  setRescheduledBooking,
  setRescheduleFlag,
  setSlotBookingFilters,
  setSlotsList,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { ROUTES } from "@/app/routes";
import { Tooltip } from "react-tooltip";
import { LuClock3 } from "react-icons/lu";
import { useNavigate } from "react-router";
import { Fragment, useState } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { Menu, Transition } from "@headlessui/react";
import Species from "@/app/components/ui/Reusable/Species";
import PetName from "@/app/components/ui/Reusable/PetName";
import { FiMoreVertical, FiRefreshCw } from "react-icons/fi";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { ICBLListData } from "@/api_framework/api_modals/CBL";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import BookingStatus from "@/app/components/ui/Reusable/BookingStatus";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useCBLListAPI } from "@/app/hooks/api_hooks/PWL/useCBLListAPI";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { IBookingCancelationModalState } from "@components/routes/modals/Cancelation";
import SendWhatsappScriptimport from "../../SlotsScreen/SendWhatsappScript";
import { IBookingSlotsFilterBody } from "@/api_framework/api_modals/Bookings";
import { toDiagnosticsReplacement } from "@/app/utils/bookingServiceSelection";
import { setPetDetails } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useIsBookingChargableAPI } from "@/app/hooks/api_hooks/bookings/useIsBookingChargableAPI";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";

type ActionItem = {
  key: string;
  label: string;
  onClick: () => void;
};

const tableHeader = [
  { item: "SN" },
  { item: "Expires In" },
  { item: "Appt Status" },
  { item: "Client Details" },
  { item: "Pet Details" },
  { item: "Service" },
  { item: "Date" },
  { item: "Time" },
  { item: "Clinic" },
  { item: "Provider" },
  { item: "Membership" },
  { item: "Comments" },
  { item: "Payment Details" },
  { item: "Created By" },
  { item: "Action" },
];

const CBLTableComponent = () => {
  const { NO_PWL_RESULTS } = useImageConstants();
  const { rescheduleAndCancelAllowedCBLFor, bookAgainAllowedCBLFor } = useArrayConstants();
  // Front desk overbooking users cannot reschedule
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING");
  const navigate = useNavigate();
  const { convertTime12HrFormat, maskMobile, truncateString, veticDateFormat } = useUtils();
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const { slotStatusAvailable } = useStringConstants();
  const { setVisible, setModalData } = useModalActions("CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_VIEW_MORE");
  const routeId = ROUTES.ROOT.children.PATIENT_WAITING_LIST.id;
  const isCBLLoading = useLoader("isPWLInProgress");
  const { routeState } = useRouteInfo(routeId);
  const { CBLList, CBLFilters } = routeState.state as IPatientWaitingListState;
  const [open, setOpen] = useState<boolean>(false);
  const [bookingId, setBookingId] = useState<string>(null);
  const fetchCBLList = useCBLListAPI();
  const { execute: fetchBookingStatus } = useIsBookingChargableAPI();
  const { setVisible: setCancel, setModalData: setCancelData } = useModalActions("APPOINTMENT_CANCELATION");
  const { setVisible: setReschedule, setModalData: setRescheduleData } = useModalActions("APPOINTMENT_RESCHEDULE");
  const { setVisible: setPaymentVisible, setModalData: setPaymentModalData } =
    useModalActions("BOOKINGS_PAYMENT_STATUS");

  const handleViewComments = (notes: string) => {
    setModalData({ appointmentNotes: notes });
    setVisible();
  };

  const handleRowSelect = (value: ICBLListData) => {
    const userId = value?.client_details?.id;
    const petId = value?.pet_details?.id;
    toast.success("User and Pet Successfully selected");
    dispatch(
      setPetDetails({
        petDetails: null,
      }),
    );
    window.open(
      `${ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url}/appointments/${userId}/${petId}`,
      "_blank",
    );
  };

  const handleBookAgain = (bookingDetails: ICBLListData) => {
    dispatch(
      setSlotsList({
        data: [],
      }),
    );
    // Prefill the booking's service, else the slots page reopens on whatever was searched last.
    dispatch(
      setSlotBookingFilters({
        filters: reconstructBookingFilters(bookingDetails),
      }),
    );
    navigate(
      `${getRouteKey("AVAILABLE_SLOTS", "url")}/${bookingDetails?.client_details?.id}/${bookingDetails?.pet_details?.id}`,
    );
  };

  const reconstructBookingFilters = (bookingDetails: ICBLListData, rescheduled = false): IBookingSlotsFilterBody => {
    // A reschedule keeps the booking's own service; booking again moves a legacy diagnostic one to Diagnostics.
    const { serviceId, subServiceId } = rescheduled
      ? { serviceId: bookingDetails?.service_details?.id, subServiceId: bookingDetails?.sub_service_details?.id }
      : toDiagnosticsReplacement(bookingDetails?.service_details?.id, bookingDetails?.sub_service_details?.id);
    return {
      booking_date: rescheduled ? bookingDetails?.booking_date : format(new Date(Date.now()), "yyyy-MM-dd"),
      clinic_ids: [],
      location_details: {
        latitude: "",
        longitude: "",
      },
      pet_species_id: bookingDetails?.pet_details?.pet_type,
      service_id: serviceId,
      staff_ids: [],
      sub_service_id: subServiceId,
      city_id: "",
      slot_status: slotStatusAvailable,
    };
  };

  const handleBookingReschedule = (bookingDetails: ICBLListData) => {
    dispatch(
      setSlotsList({
        data: [],
      }),
    );
    const filters = reconstructBookingFilters(bookingDetails, true);
    dispatch(
      setSlotBookingFilters({
        filters,
      }),
    );
    dispatch(setRescheduleFlag(true));
    dispatch(
      setRescheduledBooking({
        data: bookingDetails as any,
      }),
    );
    if (bookingDetails?.payment_details?.payment_status) {
      setReschedule();
      setRescheduleData({
        userId: bookingDetails?.client_details?.id,
        petId: bookingDetails?.pet_details?.id,
        bookingId: bookingDetails?.booking_id,
        price: bookingDetails?.payment_details?.amount,
        isRefunded: bookingDetails?.payment_details?.is_refundable,
      });
    } else {
      navigate(
        `${getRouteKey("AVAILABLE_SLOTS", "url")}/${bookingDetails?.client_details?.id}/${bookingDetails?.pet_details?.id}?type=reschedule&rbi=${bookingDetails?.booking_id}`,
      );
    }
  };

  const handleCancellation = (bookingDetails: ICBLListData) => {
    setCancel();
    setCancelData({
      CBLBookingDetails: bookingDetails,
      from: "CBL",
    } as IBookingCancelationModalState);
  };

  const getBackgroundColor = (expireTime: number) => {
    if (expireTime <= 10) return "text-[#FF0303] font-semibold";
    if (expireTime <= 30) return "text-[#C37307] font-semibold";
    return "text-[#000000] font-semibold";
  };

  const handleRefreshPaymentStatus = async (selectedBookingId: string) => {
    if (!selectedBookingId) return;
    const response = await fetchBookingStatus(selectedBookingId).catch(() => null);
    if (!response?.status) return;
    if (CBLFilters) {
      fetchCBLList(CBLFilters, "on_hold");
    }
  };

  const getActionItems = (wl: ICBLListData): ActionItem[] => {
    const items: ActionItem[] = [];
    const pushUnique = (item: ActionItem) => {
      if (!items.find((each) => each.key === item.key)) {
        items.push(item);
      }
    };
    if (wl?.appt_status?.toLowerCase() === "on hold") {
      pushUnique({
        key: "send-whatsapp",
        label: "Send Whatsapp",
        onClick: () => {
          setBookingId(wl?.booking_id);
          setOpen(true);
        },
      });
      pushUnique({
        key: "refresh-payment-status",
        label: "Payment Status",
        onClick: () => handleRefreshPaymentStatus(wl?.booking_id),
      });
      pushUnique({
        key: "cancel",
        label: "Cancel",
        onClick: () => handleCancellation(wl),
      });
      return items;
    }
    if (rescheduleAndCancelAllowedCBLFor?.includes(wl?.appt_status?.toLowerCase())) {
      if (!isFrontDeskOverbooking) {
        pushUnique({
          key: "reschedule",
          label: "Reschedule",
          onClick: () => handleBookingReschedule(wl),
        });
      }
      pushUnique({
        key: "cancel",
        label: "Cancel",
        onClick: () => handleCancellation(wl),
      });
    }
    if (bookAgainAllowedCBLFor?.includes(wl?.appt_status?.toLowerCase())) {
      pushUnique({
        key: "book-again",
        label: "Book Again",
        onClick: () => handleBookAgain(wl),
      });
    }
    return items;
  };

  const renderActions = (wl: ICBLListData, compact = false) => {
    const actionItems = getActionItems(wl);
    const primaryAction = actionItems[0];
    const menuActions = actionItems.slice(1);
    if (actionItems.length === 0) return <span className="text-xs text-gray-500" />;
    return (
      <div className="flex items-center gap-2 relative">
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            className={clsx(
              compact ? "w-[110px]" : "w-[120px]",
              "py-2 rounded-lg bg-black text-white text-xs font-medium hover:opacity-90",
            )}
          >
            {primaryAction.key === "send-whatsapp" ? "Whatsapp" : primaryAction.label}
          </button>
        )}
        {menuActions.length > 0 && (
          <Menu as="div" className="relative h-8 w-8">
            <Menu.Button className="w-full h-full flex items-center justify-center rounded-md bg-[#F1F1F1] hover:bg-gray-200 transition-colors">
              <FiMoreVertical className="w-5 h-5 text-gray-600" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-12 top-1/2 -translate-y-1/2 w-44 origin-top-right bg-white shadow-xl border border-gray-100 rounded-lg z-[80] focus:outline-none">
                <div className="py-1">
                  {menuActions.map((action) => (
                    <Menu.Item key={action.key}>
                      {({ active }) => (
                        <button
                          onClick={action.onClick}
                          className={clsx(
                            "w-full text-left px-4 py-2 text-sm transition-colors",
                            active ? "bg-gray-50 text-black" : "text-gray-700",
                          )}
                        >
                          {action.key === "refresh-payment-status" ? (
                            <span className="inline-flex items-center gap-2">
                              <FiRefreshCw className="w-3.5 h-3.5" />
                              <span>Payment Status</span>
                            </span>
                          ) : (
                            action.label
                          )}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    );
  };
  if (isCBLLoading || !Array.isArray(CBLList)) {
    return (
      <div className="px-4">
        <div className="space-y-3 md:hidden">
          <TableSkeletonComponent
            columnLength={1}
            rowLength={5}
            bodyLength={1}
            containerClassName="!mt-0 h-[260px] w-full overflow-hidden rounded-xl border border-[#ECECEC]"
          />
        </div>
        <div className="hidden md:block">
          <div className="outer-container relative mt-3 rounded-xl bg-[#F9FAFB]">
            <TableSkeletonComponent
              columnLength={15}
              rowLength={8}
              bodyLength={15}
              containerClassName="!mt-0 !h-[calc(100vh-260px)] !w-full overflow-auto rounded-xl border border-[#ECECEC]"
            />
          </div>
        </div>
      </div>
    );
  }
  if (!CBLList?.length) {
    return (
      <NoRecordFoundComponent
        imageURL={NO_PWL_RESULTS}
        message="No Appoinments Found"
        description="No appointments found for the given filters, try something else"
      />
    );
  }
  return (
    <>
      <div className="px-4">
        <div className="space-y-3 md:hidden">
          {CBLList.map((wl, id) => (
            <div key={id} className="rounded-xl border border-[#ECECEC] bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-[#1F1F23]">#{id + 1}</p>
                <p className="text-xs text-[#6C727F]">{veticDateFormat(wl?.booking_date) || "-"}</p>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <p className="text-xs text-[#6C727F]">
                  <span className="font-medium text-[#1F1F23]">Client:</span> {wl?.client_details?.name || "-"}
                </p>
                <p className="text-xs text-[#6C727F]">
                  <span className="font-medium text-[#1F1F23]">Pet:</span> {wl?.pet_details?.name || "-"}
                </p>
                <p className="text-xs text-[#6C727F]">
                  <span className="font-medium text-[#1F1F23]">Service:</span>
                  {wl?.service_details?.service_type || "-"}
                </p>
                <p className="text-xs text-[#6C727F]">
                  <span className="font-medium text-[#1F1F23]">Time:</span>
                  {convertTime12HrFormat(wl?.booking_time) || "-"}
                </p>
                <p className="text-xs text-[#6C727F]">
                  <span className="font-medium text-[#1F1F23]">Status:</span> {wl?.appt_status || "-"}
                </p>
              </div>
              <div className="mt-3">{renderActions(wl, true)}</div>
            </div>
          ))}
        </div>

        <div className="hidden md:block">
          <div className="outer-container relative mt-3 rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[calc(100vh-260px)] overscroll-y-contain">
              <div className="w-full">
                <div className="inline-block min-w-full py-1 align-middle">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[1650px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] sticky top-0 z-40 font-normal">
                        <tr>
                          {tableHeader.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className={clsx(
                                "py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize",
                                ["SN", "Date", "Time", "Service", "Provider"].includes(key.item) ? "px-2" : "px-3",
                                key.item === "SN" && "min-w-[44px]",
                                key.item === "Service" && "min-w-[140px]",
                                key.item === "Provider" && "min-w-[140px]",
                                key.item === "Clinic" && "min-w-[170px]",
                                key.item === "Action" && "min-w-[185px] sm:sticky sm:right-0 sm:z-50 bg-[#F5F5F5]",
                              )}
                            >
                              {key.item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {CBLList.map((wl, id) => {
                          const isFirst = id === 0;
                          const isLast = id === CBLList.length - 1;
                          const isSingle = CBLList.length === 1;
                          return (
                            <tr
                              key={id}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-2 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23] min-w-[44px]",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                                onClick={() => handleRowSelect(wl)}
                              >
                                {id + 1}
                              </td>
                              <td
                                className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className={clsx("flex gap-2 items-center", getBackgroundColor(wl?.expire_time))}>
                                  {wl?.expire_time === 0 ? (
                                    wl?.payment_details?.payment_status ? (
                                      <span className="text-green-600">Paid</span>
                                    ) : (
                                      <span>-</span>
                                    )
                                  ) : (
                                    <>
                                      <LuClock3 className="w-4 h-4" />
                                      <span>{wl?.expire_time} min</span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td
                                className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                                onClick={() => handleRowSelect(wl)}
                              >
                                {wl?.appt_status?.toLowerCase() === "failed_confirmed_later" ? (
                                  <>
                                    <BookingStatus bookingStatus="failed" />
                                    <span className="text-xs">(confirmed later)</span>
                                  </>
                                ) : (
                                  <BookingStatus bookingStatus={wl?.appt_status?.toLowerCase()} />
                                )}
                              </td>
                              <td
                                className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className="flex flex-col">
                                  <div
                                    data-tooltip-id="user_detail_name"
                                    data-tooltip-content={wl?.client_details?.name}
                                  >
                                    {truncateString(wl?.client_details?.name, 13)}
                                  </div>
                                  {wl?.client_details?.mobile && (
                                    <div className="text-[#0068FF] flex items-center gap-1">
                                      {maskMobile(wl?.client_details?.mobile)}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className="flex flex-col">
                                  <PetName petName={wl?.pet_details?.name} petStatus={""} />
                                  <Species
                                    className="text-[10px] text-[#808DA0]"
                                    speciesId={wl?.pet_details?.pet_type}
                                  />
                                </div>
                              </td>
                              <td
                                className="px-2 py-3 whitespace-nowrap text-xs font-normal min-w-[140px]"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className="w-full h-full flex justify-center items-start flex-col overflow-hidden">
                                  <span className="block w-full truncate">
                                    {wl?.service_details?.service_type || "-"}
                                  </span>
                                  <span className="mt-1 block w-full truncate text-[#808DA0]">
                                    {wl?.sub_service_details?.name || "-"}
                                  </span>
                                </div>
                              </td>
                              <td
                                className="px-2 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                                onClick={() => handleRowSelect(wl)}
                              >
                                {veticDateFormat(wl?.booking_date) || "-"}
                              </td>
                              <td
                                className="px-2 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                                onClick={() => handleRowSelect(wl)}
                              >
                                {convertTime12HrFormat(wl?.booking_time) || "-"}
                              </td>
                              <td
                                className="p-3 whitespace-nowrap text-xs font-normal min-w-[170px]"
                                data-tooltip-id="clinic_details_display_name"
                                data-tooltip-content={wl?.clinic_details?.name}
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className="flex items-center gap-2 max-w-full overflow-hidden">
                                  <ClipboardComponent
                                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                    copyId={`clinic-${wl?.booking_id}`}
                                    text={wl?.clinic_details?.name}
                                    tooltipText="Copy clinic name"
                                  />
                                  <span className="block w-full truncate">
                                    {truncateString(wl?.clinic_details?.name, 20)}
                                  </span>
                                </div>
                              </td>
                              <td
                                className="px-2 py-3 whitespace-nowrap text-xs font-normal min-w-[140px]"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div data-tooltip-id="staff_detail_name" data-tooltip-content={wl?.staff_details?.name}>
                                  {wl?.staff_details ? truncateString(wl?.staff_details?.name, 15) : "NA"}
                                </div>
                              </td>
                              <td
                                className="p-3 whitespace-nowrap text-xs font-normal"
                                onClick={() => handleRowSelect(wl)}
                              >
                                {wl?.membership_details?.membership ? "Member" : "Non-Member"}
                              </td>
                              <td className="p-3 whitespace-nowrap text-xs font-normal">
                                {wl?.comments !== null && wl?.comments !== "<ol><li></li></ol>" ? (
                                  <a
                                    className="text-black underline"
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewComments(wl?.comments);
                                    }}
                                  >
                                    View Comments
                                  </a>
                                ) : (
                                  <p>NA</p>
                                )}
                              </td>
                              <td className="p-3 whitespace-nowrap text-xs font-normal">
                                {wl?.appt_status?.toLowerCase() === "on hold" ? (
                                  <div className="flex items-center gap-1">
                                    <ClipboardComponent
                                      copyId={wl?.payment_details?.payment_link}
                                      text={wl?.payment_details?.payment_link}
                                      tooltipText="Copy Payment Link"
                                    />
                                    <span className="text-xs">Payment Link</span>
                                  </div>
                                ) : typeof wl?.payment_details === "object" ? (
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPaymentModalData({ bookingDetails: wl });
                                      setPaymentVisible();
                                    }}
                                    href="#"
                                    className="underline text-xs text-black"
                                  >
                                    Show payment history
                                  </a>
                                ) : (
                                  <p>{typeof wl?.payment_details === "string" ? wl?.payment_details : "NA"}</p>
                                )}
                              </td>
                              <td
                                className="p-3 whitespace-nowrap text-xs font-normal"
                                onClick={() => handleRowSelect(wl)}
                              >
                                <div className="flex flex-col">{wl?.created_by ?? "Not Available"}</div>
                              </td>
                              <td
                                className={clsx(
                                  "p-2 whitespace-normal text-xs font-normal bg-gray-100 align-middle min-w-[185px] sm:sticky sm:right-0 sm:z-10",
                                )}
                              >
                                {renderActions(wl)}
                              </td>
                            </tr>
                          );
                        })}
                        {open && (
                          <SendWhatsappScriptimport open={open} setOpen={setOpen} bookingId={bookingId} petName="" />
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="user_detail_name" />
      <Tooltip id="clinic_details_display_name" />
      <Tooltip id="staff_detail_name" />
    </>
  );
};

export default CBLTableComponent;
